import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { mailboxPath, sectionPath } from '../mail/urls';
import { avatarFor, useAvatars } from '../auth/avatars';
import { clientFor, useAccounts, type Account } from '../auth/store';
import { useT } from '../i18n';
import type { MailClient } from '../api/client';
import type { Mailbox as JmapMailbox } from '../api/types';
import { bumpRef, dropRef, useMailboxes } from '../mail/useMailboxes';
import { avatarColor, initials } from '../mail/format';
import Compose, { type ComposeTarget } from './Compose';
import Brand from './Brand';
import ConnectionBanner from './ConnectionBanner';
import FolderTree from './FolderTree';
import ThemeToggle from './ThemeToggle';
import Toasts from './Toasts';
import KeyGuide from './KeyGuide';
import { overlayOpen } from '../ui/overlays';
import { config } from '../config/app';
import { resolveSpecial } from '../mail/folders';
import { useOverlay } from '../ui/overlays';
import { usePanes } from '../ui/viewport';

/*
    The frame every screen sits in.

    Contacts, Settings and the Directory used to be separate full-page routes,
    each rebuilding its own header, its own theme and language toggles, its own
    "back to mail" link — and Contacts had a second account picker that could
    disagree with the sidebar. Three screens that behaved like three different
    programs.

    So the sidebar, the composer and the connection banner live here, once, and
    every screen renders inside them. One account, chosen in one place. The
    composer stays mounted across navigation, which also means a half-written
    message survives a trip to Settings.
*/

export type Section = 'mail' | 'contacts' | 'calendar' | 'settings';

/*
    The top bar's icons.

    Kept here rather than imported from FolderTree: that map is about mail FOLDERS and grows with
    roles the server reports. These four are about sections of the product, and the mail one is
    deliberately the same path as the inbox folder — the same thing, named the same way.
*/
const ICONS = {
    mail: 'M3 12h4l2 3h6l2-3h4M3 12l2-7h14l2 7v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z',
    calendar: 'M3.5 9.5h17M8 3.5v3M16 3.5v3',
    contacts: 'M5 20a7 7 0 0 1 14 0',
    settings:
        'M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47V21a2 2 0 1 1-4 0v-.11a1.6 1.6 0 0 0-1.05-1.46 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97H3a2 2 0 1 1 0-4h.11a1.6 1.6 0 0 0 1.46-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 .97-1.47V3a2 2 0 1 1 4 0v.11a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47.97H21a2 2 0 1 1 0 4h-.11a1.6 1.6 0 0 0-1.47.97z',
} as const;

/*
    Two of the section icons need a shape the path alone cannot carry — a calendar has a frame, a
    person has a head — so Glyph takes optional extras rather than the icons being drawn twice.
*/
function Glyph({ d, size = 15, extra }: { d: string; size?: number; extra?: React.ReactNode }) {
    return (
        <svg
            width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden style={{ flex: '0 0 auto' }}
        >
            {extra}
            <path d={d} />
        </svg>
    );
}

// The labels the sidebar grid already used, so the words on screen do not change with their place.
const SECTIONS: { id: Section; label: 'nav.mail' | 'calendar.title' | 'contacts.title'; icon: string; extra?: React.ReactNode }[] = [
    { id: 'mail', label: 'nav.mail', icon: ICONS.mail },
    { id: 'calendar', label: 'calendar.title', icon: ICONS.calendar, extra: <rect x="3.5" y="5" width="17" height="15" rx="2" /> },
    { id: 'contacts', label: 'contacts.title', icon: ICONS.contacts, extra: <circle cx="12" cy="8.5" r="3.5" /> },
];

/*
    Written the way this codebase writes every other shortcut — `Ctrl/⌘` — rather than the `⌘K` the
    design shows. There is no platform detection here, and printing a Command symbol to somebody on
    Linux is a label that names a key their keyboard does not have.
*/
const shortcutHint = 'Ctrl/⌘K';

interface MailContextValue {
    account: Account | undefined;
    client: MailClient | undefined;
    mailboxes: JmapMailbox[];
    foldersError: string | null;
    reloadFolders: () => void;
    compose: (target: ComposeTarget) => void;
    /** True while the sidebar is a drawer over the screen rather than a column beside it. */
    drawerNav: boolean;
    /** Opens that drawer. Each screen renders the button itself, in its own header. */
    openNav: () => void;

    /*
        Search lives in the shell because the box that drives it does.

        It used to live in the message list's own header, which was the right place while it was
        the only screen with a search. Now it is in the top bar, above every screen, so the text
        has to outlive the screen underneath it — and the list has to read the same value the
        person is typing into rather than keeping a second copy that drifts.
    */
    /** What is in the box, as typed. */
    searchInput: string;
    /** The same text once it has stopped changing — this is what queries run against. */
    search: string;
    setSearch: (value: string) => void;
    /** Puts the cursor in the box, wherever the person is. Ctrl/⌘+K calls this. */
    focusSearch: () => void;
}

const MailContext = createContext<MailContextValue | null>(null);

/** Everything a screen inside the shell needs. Throws outside it, on purpose. */
export function useMail(): MailContextValue {
    const value = useContext(MailContext);
    if (!value) throw new Error('useMail must be used inside <MailShell>');
    return value;
}

function Avatar({ email }: { email: string }) {
    const picture = useAvatars((s) => avatarFor(s.byEmail, email));
    const base: React.CSSProperties = {
        width: 24,
        height: 24,
        flex: '0 0 auto',
        borderRadius: 'var(--r-pill)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--fs-2xs)',
        fontWeight: 700,
        color: '#fff',
        overflow: 'hidden',
    };
    if (picture) return <img src={picture} alt="" style={{ ...base, objectFit: 'cover' }} />;
    return (
        <span style={{ ...base, background: email ? avatarColor(email) : 'var(--chip-bg)' }}>
            {email ? initials(email) : '?'}
        </span>
    );
}

/*
    The button that opens the drawer.

    It lives here, beside the drawer's own state, but it is rendered by each
    screen inside its own header — a bar of its own above the screen would cost
    a phone forty pixels of height to say something the header already says.
    Renders nothing when the sidebar is a column, because then there is nothing
    to open.
*/
export function NavButton() {
    const { drawerNav, openNav } = useMail();
    const { t } = useT();
    if (!drawerNav) return null;
    return (
        <button
            data-testid="open-nav"
            data-hit
            onClick={openNav}
            title={t('nav.folders')}
            aria-label={t('nav.folders')}
            style={{
                width: 30,
                height: 30,
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: '1px solid var(--border2)',
                borderRadius: 'var(--r-btn)',
                color: 'var(--text-dim)',
            }}
        >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
        </button>
    );
}

export default function MailShell({
    section,
    selectedMailboxId,
    children,
}: {
    section: Section;
    /** Highlighted folder. Only the mail screen has one. */
    selectedMailboxId?: string;
    children: React.ReactNode;
}) {
    const { t } = useT();
    const { acct } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const accounts = useAccounts((s) => s.accounts);
    const activeId = useAccounts((s) => s.activeId);
    const status = useAccounts((s) => s.status);
    const bootstrap = useAccounts((s) => s.bootstrap);
    const hesabiKaldir = useAccounts((s) => s.remove);
    const setActive = useAccounts((s) => s.setActive);
    const setComposing = useAccounts((s) => s.setComposing);
    const checking = useAccounts((s) => s.checking);
    const hasDAV = useAccounts((s) => s.hasDAV);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [composeTarget, setComposeTarget] = useState<ComposeTarget | null>(null);
    const { drawerNav } = usePanes();
    const [navOpen, setNavOpen] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearchDebounced] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    // Debounced here rather than in the list: the box is in the shell now, and a query per
    // keystroke against a mail server is a query per keystroke however far away the box is.
    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(searchInput), 280);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const focusSearch = useCallback(() => {
        searchRef.current?.focus();
        searchRef.current?.select();
    }, []);

    /*
        `?` opens the shortcut card, from anywhere.

        In the shell rather than in a screen because it is not about mail: wherever somebody is,
        the question "what does the keyboard do here" has the same answer. Ignored while anything
        modal is open — a question mark typed into the composer is a question mark.
    */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== '?' || overlayOpen()) return;
            const target = e.target as HTMLElement | null;
            if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? '')) return;
            e.preventDefault();
            setGuideOpen(true);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // A drawer opened on a phone must not still be open once the window is wide
    // again: it would sit on top of the sidebar that is already back.
    useEffect(() => {
        if (!drawerNav) setNavOpen(false);
    }, [drawerNav]);

    /*
        While the drawer is open it owns the keyboard, the same way the composer
        and the attachment preview do — otherwise Escape would close the open
        message underneath and leave the drawer standing over an empty list.
    */
    useOverlay(drawerNav && navOpen);
    useEffect(() => {
        if (!drawerNav || !navOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            setNavOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerNav, navOpen]);

    useEffect(() => {
        void bootstrap();
    }, [bootstrap]);

    const account = accounts.find((a) => a.id === acct) ?? accounts.find((a) => a.id === activeId) ?? accounts[0];
    const client = account ? clientFor(account.id) : undefined;

    /*
        The address bar decides which mailbox is on screen; the store is told, so that what reads
        it (the settings tabs, sign-out) speaks of the same one. And when the mailbox in the
        address bar is no longer signed in — it was signed out from another tab of Settings, or
        its session ended — the screen moves to the one that is shown instead of staying on an
        address that names something else.
    */
    useEffect(() => {
        if (account && account.id !== activeId) setActive(account.id);
    }, [account, activeId, setActive]);
    useEffect(() => {
        if (!acct || !account || acct === account.id) return;
        // Only the mailbox segment changes: the folder and the open message stay where they were.
        const rest = location.pathname.replace(/^\/m\/[^/]+/, '');
        navigate(`${mailboxPath(account.id)}${rest}${location.search}`, { replace: true });
    }, [acct, account, location.pathname, location.search, navigate]);
    /*
        No mailbox left — every session ended under this tab (a deploy ends them all). The sign-in
        form, rather than a shell with nothing in it: that is where a reload would land too.
    */
    useEffect(() => {
        if (!checking && !accounts.length) navigate('/login', { replace: true });
    }, [checking, accounts.length, navigate]);

    // The store holds an ended session back while something is being written; see sessionEnded.
    useEffect(() => {
        setComposing(composeTarget !== null);
    }, [composeTarget, setComposing]);
    useEffect(() => () => setComposing(false), [setComposing]);

    const { mailboxes, error: foldersError, reload: reloadFolders } = useMailboxes(account?.id);
    const special = useMemo(() => resolveSpecial(mailboxes), [mailboxes]);

    const value = useMemo<MailContextValue>(
        () => ({
            account,
            client,
            mailboxes,
            foldersError,
            reloadFolders,
            compose: setComposeTarget,
            drawerNav,
            openNav: () => setNavOpen(true),
            searchInput,
            search,
            setSearch: setSearchInput,
            focusSearch,
        }),
        [account, client, mailboxes, foldersError, reloadFolders, drawerNav, searchInput, search, focusSearch],
    );

    // Every section keeps the account in its path, so switching mailbox from any
    // screen keeps you on the screen you were on.
    const to = (next: Section) => {
        if (!account) return '/login';
        return next === 'mail' ? mailboxPath(account.id) : sectionPath(account.id, next);
    };

    return (
        <MailContext.Provider value={value}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
                <ConnectionBanner />

                {/*
                    The top bar.

                    Sections used to live at the BOTTOM of the sidebar, in a 2x2 grid of 10px
                    monospace labels. They are the primary navigation of the product and they sat
                    below the folder list, under a storage meter — which is the hierarchy upside
                    down. Up here they read as what they are, and the sidebar is left to do one
                    job: folders.

                    The tenant's name rides along on purpose. In a multi-tenant system the
                    question "whose mailbox am I in" is asked constantly, and the answer belongs
                    somewhere it cannot scroll away.
                */}
                <header
                    style={{
                        flex: '0 0 auto',
                        height: 52,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20,
                        padding: '0 14px 0 16px',
                        background: 'var(--surface)',
                        borderBottom: '1px solid var(--border)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: '0 0 auto' }}>
                        <Brand size={22} />
                        {/*
                            Drawn as real text in the application's own font, not as the packaged
                            horizontal lockup. That file carries the name inside <text>, and an
                            <img> cannot reach the page's fonts — the brand package says so itself.
                        */}
                        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.6px' }}>
                            <span style={{ color: 'var(--accent)' }}>sum</span>
                            <span style={{ color: 'var(--text)' }}>mail</span>
                        </span>
                    </div>

                    {!drawerNav ? (
                        <>
                            <div style={{ width: 1, height: 22, background: 'var(--border)', flex: '0 0 auto' }} />
                            <nav style={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
                                {SECTIONS.filter((entry) => entry.id !== 'contacts' || hasDAV)
                                    .filter((entry) => entry.id !== 'calendar' || hasDAV)
                                    .map((entry) => {
                                        const on = section === entry.id;
                                        return (
                                            <button
                                                key={entry.id}
                                                data-testid={`nav-${entry.id}`}
                                                data-hit
                                                onClick={() => navigate(to(entry.id))}
                                                style={{
                                                    height: 32,
                                                    padding: '0 13px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 7,
                                                    background: on ? 'var(--accent-bg)' : 'transparent',
                                                    border: 'none',
                                                    borderRadius: 'var(--r-btn)',
                                                    color: on ? 'var(--accent-strong)' : 'var(--text-dim)',
                                                    fontSize: 'var(--fs-md)',
                                                    fontWeight: on ? 600 : 500,
                                                }}
                                            >
                                                <Glyph d={entry.icon} extra={entry.extra} />
                                                {t(entry.label)}
                                            </button>
                                        );
                                    })}
                            </nav>
                        </>
                    ) : null}

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 60 }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: 520 }}>
                            <svg
                                width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-mute)"
                                strokeWidth="1.8" strokeLinecap="round"
                                style={{ position: 'absolute', left: 11, pointerEvents: 'none' }}
                            >
                                <circle cx="11" cy="11" r="6.5" />
                                <path d="M16 16l4.5 4.5" />
                            </svg>
                            <input
                                ref={searchRef}
                                data-testid="search"
                                data-hit
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    // Escape clears and steps out, the way it did while the box
                                    // lived in the list header. The box moved; the habit did not.
                                    if (e.key === 'Escape') {
                                        setSearchInput('');
                                        e.currentTarget.blur();
                                    }
                                }}
                                onFocus={() => {
                                    // Typing here from the calendar or the address book means mail:
                                    // that is where results can be shown.
                                    if (section !== 'mail') navigate(to('mail'));
                                }}
                                placeholder={t('list.searchPlaceholder')}
                                spellCheck={false}
                                aria-label={t('list.searchPlaceholder')}
                                style={{
                                    width: '100%',
                                    height: 34,
                                    // Room for the hint when it is there, none reserved when it
                                    // is not: at 62px the longer `Ctrl/⌘K` sat on top of the
                                    // placeholder — measured on a 640px window.
                                    padding: drawerNav ? '0 12px 0 34px' : '0 80px 0 34px',
                                    background: 'var(--bg)',
                                    border: '1px solid var(--border2)',
                                    borderRadius: 'var(--r-input)',
                                    color: 'var(--text)',
                                    fontSize: 'var(--fs-md)',
                                }}
                            />
                            {/*
                                Not drawn where it cannot be used. Below the drawer width the
                                screen is a phone and a keyboard hint is decoration that costs
                                the placeholder its room.
                            */}
                            {!drawerNav ? (
                            <span
                                aria-hidden
                                style={{
                                    position: 'absolute',
                                    right: 10,
                                    fontFamily: 'var(--font-mono)',
                                    fontSize: 'var(--fs-2xs)',
                                    letterSpacing: '0.5px',
                                    padding: '2px 6px',
                                    borderRadius: 'var(--r-badge)',
                                    background: 'var(--chip-bg)',
                                    color: 'var(--text-mute)',
                                }}
                            >
                                {shortcutHint}
                            </span>
                            ) : null}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', position: 'relative' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                height: 34,
                                padding: '0 4px 0 10px',
                                background: 'var(--bg)',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-input)',
                            }}
                        >
                            {/*
                                Not on a phone. The name pushed this group past the edge of a 390px
                                screen (measured: the page grew to 487px), the browser zoomed the
                                whole page out to fit, and the drawer's lower buttons landed below
                                the visible screen — which is why the address book tab "could not
                                be clicked" on the two addresses that carry a name. The name is
                                still on the sign-in screen, where it introduces the operator.
                            */}
                            {config().operatorName && !drawerNav ? (
                                <span
                                    style={{
                                        fontSize: 'var(--fs-sm)',
                                        color: 'var(--text-dim)',
                                        maxWidth: 150,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {config().operatorName}
                                </span>
                            ) : null}
                            {config().operatorName && !drawerNav ? <span style={{ width: 1, height: 16, background: 'var(--border)' }} /> : null}
                            <button
                                data-testid="account-switcher"
                                data-hit
                                onClick={() => setSwitcherOpen((v) => !v)}
                                /*
                                    Named for what it DOES, with the address as detail. Labelling
                                    it with the bare address said nothing about the button — and
                                    it collided with every other control whose label happens to
                                    end in the same address, which is how the browser suite ended
                                    up opening this menu while reaching for a recipient chip.
                                */
                                aria-label={`${t('account.switchOrAdd')} — ${account?.email ?? t('login.addMailbox')}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 7,
                                    height: 26,
                                    padding: '0 5px 0 3px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: 8,
                                }}
                            >
                                <Avatar email={account?.email ?? ''} />
                                {status[account?.id ?? ''] === 'error' ? (
                                    <span style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>!</span>
                                ) : null}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-mute)" strokeWidth="2" strokeLinecap="round">
                                    <path d={switcherOpen ? 'M7 14l5-5 5 5' : 'M7 10l5 5 5-5'} />
                                </svg>
                            </button>
                        </div>
                        <ThemeToggle />
                        <button
                            data-testid="nav-settings"
                            data-hit
                            onClick={() => navigate(to('settings'))}
                            title={t('app.settings')}
                            aria-label={t('app.settings')}
                            style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto',
                                background: section === 'settings' ? 'var(--accent-bg)' : 'transparent',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-btn)',
                                color: section === 'settings' ? 'var(--accent-strong)' : 'var(--text-dim)',
                            }}
                        >
                            <Glyph d={ICONS.settings} size={14} />
                        </button>

                {switcherOpen ? (
                    <>
                        {/* Click-away catcher; sits under the menu, over everything else. */}
                        <div onClick={() => setSwitcherOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 19 }} />
                        <div
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 40,
                                minWidth: 260,
                                zIndex: 20,
                                padding: 6,
                                background: 'var(--surface)',
                                border: '1px solid var(--border2)',
                                borderRadius: 'var(--r-btn)',
                                boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                            }}
                        >
                            {accounts.map((a) => {
                                const on = a.id === account?.id;
                                return (
                                    <button
                                        key={a.id}
                                        onClick={() => {
                                            setSwitcherOpen(false);
                                            setNavOpen(false);
                                            // Stay on this screen, with the other mailbox.
                                            navigate(section === 'mail' ? mailboxPath(a.id) : sectionPath(a.id, section));
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 9,
                                            width: '100%',
                                            padding: '6px 8px',
                                            textAlign: 'left',
                                            background: on ? 'var(--accent-bg)' : 'transparent',
                                            border: 'none',
                                            borderRadius: 8,
                                        }}
                                    >
                                        <Avatar email={a.email} />
                                        <span
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: 'var(--fs-sm)',
                                                fontWeight: on ? 600 : 400,
                                                color: on ? 'var(--text)' : 'var(--text-dim)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {a.email}
                                        </span>
                                        {status[a.id] === 'error' ? <span style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>!</span> : null}
                                    </button>
                                );
                            })}
                            <div style={{ height: 1, background: 'var(--border)', margin: '6px 2px' }} />
                            <button
                                onClick={() => {
                                    setSwitcherOpen(false);
                                    setNavOpen(false);
                                    navigate('/login');
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 9,
                                    width: '100%',
                                    padding: '6px 8px',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: 8,
                                    color: 'var(--text-dim)',
                                    fontSize: 'var(--fs-sm)',
                                }}
                            >
                                <span style={{ width: 24, textAlign: 'center', color: 'var(--accent)' }}>+</span>
                                {t('login.addMailbox')}
                            </button>
                            {/*
                                Çıkış BURADA, ve buraya bir kullanıcı arayıp bulamadığı için kondu.

                                Tek çıkış yolu Ayarlar → Hesaplar'a gömülüydü. İnsanlar çıkışı
                                hesaplarının olduğu yerde arar: sağ üstteki bu menüde. Orada
                                olmayınca "çıkış yok" gibi görünüyordu — ve öyle görünen bir şey,
                                pratikte yok demektir.
                            */}
                            {account ? (
                                <button
                                    data-testid="cikis-yap"
                                    onClick={() => {
                                        setSwitcherOpen(false);
                                        setNavOpen(false);
                                        // Only the mailbox on screen signs out; if another is still
                                        // signed in, the screen moves to it rather than to the form.
                                        const kalan = accounts.find((a) => a.id !== account.id);
                                        hesabiKaldir(account.id);
                                        navigate(kalan ? mailboxPath(kalan.id) : '/login');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 9,
                                        width: '100%',
                                        padding: '6px 8px',
                                        textAlign: 'left',
                                        background: 'transparent',
                                        border: 'none',
                                        borderRadius: 8,
                                        color: 'var(--text-dim)',
                                        fontSize: 'var(--fs-sm)',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <span style={{ width: 24, textAlign: 'center' }} aria-hidden="true">⏻</span>
                                    {t('accounts.signOut')}
                                </button>
                            ) : null}
                        </div>
                    </>
                ) : null}
                    </div>
                </header>
                <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
                    {/*
                        Below the width where a sidebar and a readable message fit
                        side by side, the sidebar stops being a column and becomes
                        a drawer over the screen.

                        The same markup either way, deliberately. A second folder
                        tree written for phones is a second thing to keep in step
                        with the first, and those two always drift apart.
                    */}
                    {drawerNav && navOpen ? (
                        <div
                            data-testid="nav-scrim"
                            onClick={() => setNavOpen(false)}
                            style={{ position: 'fixed', inset: 0, zIndex: 29, background: 'rgba(0,0,0,0.45)' }}
                        />
                    ) : null}
                    <nav
                        /* Closed, it is off the screen — and out of the tab order, which
                           moving it alone would not do. */
                        inert={drawerNav && !navOpen}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: '1px solid var(--border)',
                            background: 'var(--bg)',
                            ...(drawerNav
                                ? {
                                      position: 'fixed',
                                      top: 0,
                                      bottom: 0,
                                      left: 0,
                                      width: 'min(236px, 82vw)',
                                      zIndex: 30,
                                      transform: navOpen ? 'none' : 'translateX(-100%)',
                                      transition: 'transform 160ms',
                                      boxShadow: navOpen ? '0 0 40px rgba(0,0,0,0.5)' : 'none',
                                  }
                                : { width: 236, flex: '0 0 auto' }),
                        }}
                    >

                        <div style={{ padding: '0 12px' }}>
                            <button
                                data-testid="compose"
                                data-hit
                                onClick={() => {
                                    setNavOpen(false);
                                    setComposeTarget({ mode: 'new', source: null });
                                }}
                                style={{
                                    width: '100%',
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                    background: 'var(--accent)',
                                    color: 'var(--on-accent)',
                                    border: 'none',
                                    borderRadius: 'var(--r-btn)',
                                    fontSize: 'var(--fs-md)',
                                    fontWeight: 600,
                                }}
                            >
                                {t('action.compose')}
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', opacity: 0.7 }}>c</span>
                            </button>
                        </div>


                        {foldersError ? (
                            <div style={{ padding: '0 12px 8px', fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{foldersError}</div>
                        ) : null}

                        <FolderTree
                            client={client}
                            accountId={account?.accountId}
                            accountKey={account?.id ?? ''}
                            mailboxes={mailboxes}
                            selectedId={section === 'mail' ? selectedMailboxId : undefined}
                            onSelect={(id) => {
                                setNavOpen(false);
                                if (account) navigate(mailboxPath(account.id, id));
                            }}
                            onChanged={reloadFolders}
                            onMoveMessage={(emailId, mailboxId) => dropRef.current?.(emailId, mailboxId)}
                        />

                        {/*
                            A grid, not one long row: the meter is a full-width bar,
                            and four labels plus two controls do not fit across a
                            254px sidebar in Turkish. The nav wraps instead of
                            pushing the theme toggle out from under the pointer.
                        */}
                        {/*
                            Sections, but ONLY while the sidebar is a drawer.

                            Above 680 they live in the top bar, where primary navigation belongs.
                            Below it the top bar has no room for them, so they come here — which is
                            the one place already open on a phone. Two copies of the same links,
                            never both visible.
                        */}
                        {drawerNav ? (
                            <div style={{ borderTop: '1px solid var(--border)', padding: '8px 12px 10px', display: 'grid', gap: 2 }}>
                                {SECTIONS.filter((entry) => entry.id === 'mail' || hasDAV).map((entry) => {
                                    const on = section === entry.id;
                                    return (
                                        <button
                                            key={entry.id}
                                            data-testid={`nav-${entry.id}`}
                                            data-hit
                                            onClick={() => {
                                                setNavOpen(false);
                                                navigate(to(entry.id));
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 9,
                                                width: '100%',
                                                padding: '9px 9px',
                                                textAlign: 'left',
                                                background: on ? 'var(--accent-bg)' : 'transparent',
                                                border: 'none',
                                                borderRadius: 'var(--r-btn)',
                                                color: on ? 'var(--accent-strong)' : 'var(--text-dim)',
                                                fontSize: 'var(--fs-md)',
                                                fontWeight: on ? 600 : 500,
                                            }}
                                        >
                                            <Glyph d={entry.icon} extra={entry.extra} />
                                            {t(entry.label)}
                                        </button>
                                    );
                                })}
                                <button
                                    data-testid="nav-settings-drawer"
                                    data-hit
                                    onClick={() => {
                                        setNavOpen(false);
                                        navigate(to('settings'));
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 9,
                                        width: '100%',
                                        padding: '9px 9px',
                                        textAlign: 'left',
                                        background: section === 'settings' ? 'var(--accent-bg)' : 'transparent',
                                        border: 'none',
                                        borderRadius: 'var(--r-btn)',
                                        color: section === 'settings' ? 'var(--accent-strong)' : 'var(--text-dim)',
                                        fontSize: 'var(--fs-md)',
                                        fontWeight: section === 'settings' ? 600 : 500,
                                    }}
                                >
                                    <Glyph d={ICONS.settings} />
                                    {t('app.settings')}
                                </button>
                            </div>
                        ) : null}
                    </nav>

                    <div style={{ flex: 1, display: 'flex', minWidth: 0 }}>{children}</div>
                </div>

                {composeTarget ? (
                    <Compose
                        client={client}
                        accountId={account?.accountId}
                        target={composeTarget}
                        draftsMailboxId={special.byRole.drafts?.id ?? null}
                        sentMailboxId={special.byRole.sent?.id ?? null}
                        onClose={() => setComposeTarget(null)}
                        onSent={() => {
                            setComposeTarget(null);
                            reloadFolders();
                            // A sent message belongs in the list behind the composer.
                            bumpRef.current?.();
                        }}
                    />
                ) : null}

                {guideOpen ? <KeyGuide onClose={() => setGuideOpen(false)} /> : null}

                {/* Above the screen, below the composer: the way back from the last action. */}
                <Toasts />
            </div>
        </MailContext.Provider>
    );
}
